/**
 * 几何工具 - 用于拾取模式判断点击是否在当前 AOI 多边形内 (软提示用, 不做强限制)。
 */

/**
 * 判断经纬度点 (lng, lat) 是否落在 GeoJSON 多边形内。
 *
 * <p>支持 Polygon + MultiPolygon, 只看外环 (忽略内环 hole; 业务上 AOI 一般没洞)。
 * 用射线法 (ray casting), 边界点视为 "外" — 不严格 (业务上够用, 不引 turf 依赖)。
 *
 * @param lng              经度
 * @param lat              纬度
 * @param geometryGeoJson  GeoJSON 字符串 (e.g. selfGeometry.geometryGeoJson); 解析失败 / 空 → 返回 true (不报警)
 */
export function isPointInPolygon(lng: number, lat: number, geometryGeoJson: string | null | undefined): boolean {
    if (!geometryGeoJson) return true;
    let geom: { type: string; coordinates: unknown };
    try {
        geom = JSON.parse(geometryGeoJson);
    } catch {
        return true;
    }
    if (!geom || typeof geom !== 'object') return true;

    if (geom.type === 'Polygon') {
        const coords = geom.coordinates as number[][][];
        if (!coords[0]) return true;
        return pointInRing(lng, lat, coords[0]);
    }
    if (geom.type === 'MultiPolygon') {
        const polys = geom.coordinates as number[][][][];
        // 落在任意一个 polygon 的外环里都算 in
        return polys.some((poly) => poly[0] && pointInRing(lng, lat, poly[0]));
    }
    return true;
}

/**
 * 射线法 — 判断点 (x=lng, y=lat) 是否在闭合 ring (一组 [lng, lat] 顶点) 内部。
 *
 * <p>实现细节: 水平向右射一条射线, 数跟环边相交几次, 奇数次 = 内, 偶数次 = 外。
 */
function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i][0];
        const yi = ring[i][1];
        const xj = ring[j][0];
        const yj = ring[j][1];
        const intersect = ((yi > lat) !== (yj > lat))
            && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}
